
import { useEffect, useCallback } from 'react';

const STORAGE_KEY = 'website_chat_widget';

interface LanguageTexts {
  mainLabel: string;
  startButtonText: string;
  endButtonText: string;
  emptyChatMessage: string;
  emptyVoiceMessage: string;
}

interface ChatWidgetConfig {
  texts: Record<string, LanguageTexts>;
  appearance: {
    theme: string;
    size: string;
    position: string;
    accentColor: string;
    buttonBaseColor: string;
    buttonAccentColor: string;
  };
}

function applyWidgetConfig(config: ChatWidgetConfig, lang?: string) {
  // Now handled purely by React state in SupportWidget.tsx!
  // Emitting the event just incase anything still needs it
  window.dispatchEvent(new Event('chat_widget_updated'));
}

export function getStoredConfig(): ChatWidgetConfig | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    // ignore
  }
  return null;
}

export function applySavedWidgetSettings(lang?: string) {
  const config = getStoredConfig();
  if (config) {
    applyWidgetConfig(config, lang);
  }
}

export default function useChatWidgetSync() {
  const syncWidget = useCallback((lang?: string) => {
    applySavedWidgetSettings(lang);
  }, []);

  useEffect(() => {
    // Listen for save events from ChatWidgetEditor
    const handleUpdate = () => {
      setTimeout(() => syncWidget(), 100);
    };
    window.addEventListener('chat_widget_updated', handleUpdate);
    
    // Listen for language changes
    const handleLangChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      const newLang = customEvent.detail?.lang;
      if (newLang) {
        setTimeout(() => syncWidget(newLang), 100);
      }
    };
    window.addEventListener('language_changed', handleLangChange);

    // Apply on mount with small delay to ensure widget script is loaded
    const timer = setTimeout(() => syncWidget(), 1500);

    return () => {
      window.removeEventListener('chat_widget_updated', handleUpdate);
      window.removeEventListener('language_changed', handleLangChange);
      clearTimeout(timer);
    };
  }, [syncWidget]);

  return { syncWidget };
}
